import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, CalendarDays, Check, CheckCircle2, Clock, MapPin, UserRound, Users } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { API, fmtEventDate, fmtEventTime, fmtPrice } from "../lib/eventUtils";
import { DPS_MEDIA } from "../lib/content";
import { WaitlistForm } from "../components/site/WaitlistForm";
import { trackEvent } from "../lib/analytics";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", instagram: "" };

export default function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoErr, setPromoErr] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [refCode, setRefCode] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/events/${slug}`);
      setEvent(data);
    } catch {
      setNotFound(true);
    }
  };

  useEffect(() => {
    setEvent(null);
    setNotFound(false);
    setDone(null);
    setError("");
    setForm(EMPTY);
    setPromo(null);
    setPromoErr("");
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      const code = ref.trim().toUpperCase();
      setPromoInput(code);
      setRefCode(code);
      axios.post(`${API}/api/affiliates/click`, { code, path: window.location.pathname }).catch(() => {});
    } else {
      setPromoInput("");
      setRefCode(null);
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const applyCode = async (codeArg) => {
    const code = (codeArg || promoInput).trim().toUpperCase();
    if (!code) return;
    setPromoBusy(true);
    setPromoErr("");
    try {
      const { data } = await axios.post(`${API}/api/affiliates/validate`, { code, event_slug: slug });
      setPromo(data);
    } catch (err) {
      setPromo(null);
      setPromoErr(err.response?.data?.detail || "Invalid code");
    } finally {
      setPromoBusy(false);
    }
  };

  useEffect(() => {
    if (event && event.price_cents > 0 && refCode) {
      applyCode(refCode);
      setRefCode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, refCode]);

  useEffect(() => {
    if (!event) return undefined;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: event.title,
      startDate: event.date_start,
      endDate: new Date(new Date(event.date_start).getTime() + event.duration_minutes * 60000).toISOString(),
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      description: event.description,
      location: { "@type": "Place", name: event.location_name, address: event.location_address },
      organizer: { "@type": "Organization", name: event.partner },
      performer: { "@type": "Person", name: event.led_by },
      maximumAttendeeCapacity: event.capacity,
      image: event.backdrop_poster ? window.location.origin + event.backdrop_poster : undefined,
      offers: {
        "@type": "Offer",
        url: window.location.href,
        availability: event.spots_remaining > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
      },
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = "event-jsonld";
    tag.text = JSON.stringify(ld);
    document.head.appendChild(tag);
    return () => tag.remove();
  }, [event]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (event.price_cents > 0) {
        const { data } = await axios.post(`${API}/api/events/${slug}/checkout`, {
          ...form,
          instagram: form.instagram || null,
          origin_url: window.location.origin,
          affiliate_code: promo?.code || null,
        });
        if (data.checkout_url) {
          trackEvent("event_checkout_started", { event: slug });
          window.location.href = data.checkout_url;
          return;
        }
        setDone(data);
        trackEvent("event_registration", { event: slug });
        load();
        setBusy(false);
        return;
      }
      const { data } = await axios.post(`${API}/api/events/${slug}/register`, {
        ...form,
        instagram: form.instagram || null,
      });
      setDone(data);
      trackEvent("event_registration", { event: slug });
      load();
      setBusy(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — please try again.");
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div className="pt-16 min-h-[60vh] flex flex-col items-center justify-center gap-5" data-testid="event-not-found">
        <h1 className="font-head font-black uppercase text-3xl">Event not found</h1>
        <Link to="/events" className="btn-volt" data-testid="event-notfound-back">
          <ArrowLeft size={16} /> All Events
        </Link>
      </div>
    );
  }
  if (!event) {
    return (
      <div className="pt-16 min-h-[60vh] flex items-center justify-center" data-testid="event-loading">
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-zinc-500 animate-pulse">Loading event…</p>
      </div>
    );
  }

  const isDps = /dallas performance/i.test(event.partner);
  const full = event.spots_remaining <= 0;
  const closed = !event.registration_open;
  const paid = event.price_cents > 0;
  const effectiveCents = paid ? (promo ? promo.discounted_cents : event.price_cents) : 0;
  const pct = Math.min((event.registered_count / event.capacity) * 100, 100);

  return (
    <div data-testid="event-detail-page" className="pt-16">
      <Seo
        title={`${event.title} — ${fmtEventDate(event.date_start)} | HyroxNinja`}
        description={`${event.title} at ${event.location_name}, ${event.location_address} — ${fmtEventDate(event.date_start)} at ${fmtEventTime(event.date_start)}. ${event.duration_minutes} minutes, led by ${event.led_by}. Capacity ${event.capacity}. Sign up on HyroxNinja.`}
      />

      {/* VIDEO HERO */}
      <section className="relative overflow-hidden">
        {event.backdrop_video && (
          <video
            src={event.backdrop_video}
            poster={event.backdrop_poster || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
            data-testid="event-hero-video"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-24 pb-16">
          <Link to="/events" className="inline-flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-volt transition-colors mb-6" data-testid="event-back-link">
            <ArrowLeft size={13} /> All Events
          </Link>
          <p className="inline-flex w-fit font-mono2 text-[10px] uppercase tracking-[0.25em] border border-volt/60 text-volt px-3 py-1.5 mb-5 block" data-testid="event-partner-badge">
            {event.partner}
          </p>
          <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-7">
            <MaskedLine delay={0.1}>{event.title}</MaskedLine>
          </h1>
          <div className="flex flex-wrap gap-x-7 gap-y-3 mb-7 font-mono2 text-[11px] uppercase tracking-[0.18em] text-zinc-300" data-testid="event-meta">
            <span className="flex items-center gap-2"><CalendarDays size={14} className="text-volt" /> {fmtEventDate(event.date_start)}</span>
            <span className="flex items-center gap-2"><Clock size={14} className="text-volt" /> {fmtEventTime(event.date_start)} · {event.duration_minutes} min</span>
            {event.led_by && <span className="flex items-center gap-2"><UserRound size={14} className="text-volt" /> Led by {event.led_by}</span>}
            <span className="flex items-center gap-2"><MapPin size={14} className="text-volt" /> {event.location_name}</span>
          </div>
          <div className="max-w-md" data-testid="event-capacity-bar">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
                <Users size={13} /> {event.registered_count} of {event.capacity} spots filled
              </span>
              <span className={`font-head font-extrabold uppercase text-sm ${full ? "text-red-400" : "text-volt"}`} data-testid="event-spots-remaining">
                {full ? "Sold Out" : `${event.spots_remaining} left`}
              </span>
            </div>
            <div className="h-1.5 bg-white/10">
              <div className="h-full bg-volt transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </section>

      {/* DETAILS + SIGNUP */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-3">
          <Reveal>
            <p className="eyebrow mb-3">The Session</p>
            <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-4">What to expect</h2>
            <p className="text-zinc-400 leading-relaxed mb-8 max-w-2xl" data-testid="event-description">{event.description}</p>
          </Reveal>
          {event.notes?.length > 0 && (
            <Reveal>
              <div className="border border-white/10 bg-[#0c0c0c] p-6 mb-8" data-testid="event-notes-card">
                <p className="eyebrow mb-4">Before you arrive</p>
                <ul className="space-y-3">
                  {event.notes.map((n) => (
                    <li key={n} className="flex items-start gap-3 text-zinc-300 text-sm">
                      <Check size={15} className="text-volt mt-0.5 shrink-0" /> {n}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}
          <Reveal>
            <div className="border border-white/10 bg-[#0c0c0c] p-6" data-testid="event-location-card">
              <p className="eyebrow mb-3">Location</p>
              <h3 className="font-head font-bold uppercase text-xl mb-1">{event.location_name}</h3>
              <p className="text-zinc-400 text-sm mb-4">{event.location_address}</p>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(`${event.location_name} ${event.location_address}`)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-volt font-mono2 text-xs uppercase tracking-[0.2em] hover:gap-3 transition-all"
                data-testid="event-directions-link"
              >
                Get Directions <ArrowRight size={14} />
              </a>
            </div>
          </Reveal>
        </div>

        {/* SIGNUP CARD */}
        <Reveal className="lg:col-span-2 lg:sticky lg:top-24">
          <div className="border border-volt/40 bg-[#0c0c0c]" data-testid="event-signup-card">
            <div className="bg-volt text-black px-6 py-3 flex items-center justify-between">
              <span className="font-head font-extrabold uppercase tracking-tight">{full && !closed ? "Join the waitlist" : "Reserve your spot"}</span>
              <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="event-price-chip">
                {full ? "Full" : paid ? `${fmtPrice(event.price_cents)} · ${event.spots_remaining} open` : `${event.spots_remaining} open`}
              </span>
            </div>
            <div className="p-6">
              {done ? (
                <div className="text-center py-6" data-testid="event-signup-success">
                  <CheckCircle2 size={44} className="text-volt mx-auto mb-4" />
                  <h3 className="font-head font-extrabold uppercase text-2xl mb-2">You're in!</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                    Your spot for <span className="text-white font-semibold">{event.title}</span> is confirmed —
                    you are registration <span className="text-volt font-semibold">#{done.registered_count}</span> of {event.capacity}.
                  </p>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    {fmtEventDate(event.date_start)} · {fmtEventTime(event.date_start)} · arrive by 7:50 AM
                  </p>
                </div>
              ) : closed ? (
                <div className="text-center py-6" data-testid="event-signup-closed">
                  <p className="font-head font-extrabold uppercase text-2xl mb-2">Registration closed</p>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    Registration is not open for this event right now. Check back soon.
                  </p>
                </div>
              ) : full ? (
                <WaitlistForm slug={slug} waitlistCount={event.waitlist_count || 0} onJoined={load} />
              ) : (
                <form onSubmit={submit} data-testid="event-signup-form">
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <label className="block">
                      <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">First Name *</span>
                      <input required value={form.first_name} onChange={set("first_name")} placeholder="Alex" className="volt-input" data-testid="signup-first-name" />
                    </label>
                    <label className="block">
                      <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Last Name *</span>
                      <input required value={form.last_name} onChange={set("last_name")} placeholder="Rivera" className="volt-input" data-testid="signup-last-name" />
                    </label>
                  </div>
                  <label className="block mb-4">
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Email *</span>
                    <input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="volt-input" data-testid="signup-email" />
                  </label>
                  <label className="block mb-4">
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Phone *</span>
                    <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="(214) 555-0123" className="volt-input" data-testid="signup-phone" />
                  </label>
                  <label className="block mb-5">
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Instagram (optional)</span>
                    <input value={form.instagram} onChange={set("instagram")} placeholder="@yourhandle" className="volt-input" data-testid="signup-instagram" />
                  </label>
                  {paid && (
                    <div className="mb-5" data-testid="promo-code-block">
                      <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Discount / Affiliate Code (optional)</span>
                      <div className="flex gap-2">
                        <input
                          value={promoInput}
                          onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromo(null); setPromoErr(""); }}
                          placeholder="CODE"
                          className="volt-input flex-1 uppercase"
                          data-testid="promo-code-input"
                        />
                        <button type="button" onClick={() => applyCode()} disabled={promoBusy || !promoInput} className="btn-ghost !py-2 !px-4 !text-xs disabled:opacity-40" data-testid="promo-apply-button">
                          {promoBusy ? "…" : "Apply"}
                        </button>
                      </div>
                      {promo && (
                        <p className="text-volt text-xs mt-2 font-mono2 uppercase tracking-[0.1em]" data-testid="promo-applied">
                          {promo.code} applied — {promo.discounted_cents === 0 ? "your spot is free" : `you pay ${fmtPrice(promo.discounted_cents)} (was ${fmtPrice(promo.original_cents)})`}
                        </p>
                      )}
                      {promoErr && <p className="text-red-300 text-xs mt-2" data-testid="promo-error">{promoErr}</p>}
                    </div>
                  )}
                  {error && (
                    <p className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-4" data-testid="signup-error">
                      {error}
                    </p>
                  )}
                  <button type="submit" disabled={busy} className="btn-volt w-full justify-center disabled:opacity-60" data-testid="signup-submit">
                    {busy
                      ? paid && effectiveCents > 0 ? "Heading to checkout…" : "Reserving…"
                      : paid
                        ? effectiveCents === 0
                          ? `Free with ${promo.code} — Claim My Spot`
                          : `Pay ${fmtPrice(effectiveCents)} & Claim My Spot`
                        : "Claim My Spot"}{" "}
                    {!busy && <ArrowRight size={16} />}
                  </button>
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-zinc-600 mt-4 text-center">
                    {paid
                      ? effectiveCents === 0
                        ? `Free with code ${promo.code} · ${event.capacity}-person cap · confirmation on screen`
                        : `${fmtPrice(effectiveCents)} · secure Stripe checkout · spot confirmed after payment`
                      : `Free to register · ${event.capacity}-person cap · confirmation on screen`}
                  </p>
                </form>
              )}
            </div>
          </div>
        </Reveal>
      </section>

      {/* DPS FOOTAGE */}
      {isDps && (
        <>
          <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-14" data-testid="event-footage-strip">
            <Reveal>
              <p className="eyebrow mb-3">Inside DPS</p>
              <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-8">Train where it's real</h2>
            </Reveal>
            <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {DPS_MEDIA.strip.map((src, i) => (
                <Reveal key={src} delay={i * 0.06} className="bg-black">
                  <div className="aspect-video bg-black">
                    <video src={src} autoPlay muted loop playsInline preload="metadata" className="w-full h-full object-cover" data-testid={`event-footage-${i}`} />
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
          <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="event-community-section">
            <Reveal>
              <div className="relative border border-white/10 overflow-hidden">
                <video src={DPS_MEDIA.group} poster={DPS_MEDIA.groupPoster} autoPlay muted loop playsInline preload="metadata" className="w-full max-h-[60vh] object-cover" data-testid="event-community-video" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent flex items-end">
                  <div className="p-7 lg:p-10">
                    <p className="eyebrow mb-2">The Crew</p>
                    <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white mb-2">24 athletes. One Sunday.</h2>
                    <p className="text-zinc-300 max-w-xl text-sm leading-relaxed">
                      DPS Sundays run as one wave — partner pushes, shared pain, team finish. Claim a spot and bring your crew.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </section>
        </>
      )}
    </div>
  );
}
